/**
 * بازسازی برآورد.
 *
 * ⚠️ بازسازی یک **مشاوره‌ی تازه** است و از کل خط لوله می‌گذرد. نتیجه‌ی قبلی به
 * مدل داده نمی‌شود و بازیابی حذف نمی‌شود. رکورد قبلی هم بازنویسی نمی‌شود؛
 * رابطه با `parent_consultation_id` و `attempt` در لاگ append-only می‌ماند.
 *
 * ⚠️ اما **پاسخ‌های شفاف‌سازی** منتقل می‌شوند. سرور آن‌ها را بر اساس هویت پرسش
 * به پرسش‌های تحلیل تازه نگاشت می‌کند و اگر پرسش تازه‌ای بماند، پیش از ساخت
 * برآورد می‌ایستد و همان را می‌پرسد. فرستادن فهرست خالی یعنی «ویرایشی ندارم»،
 * نه «پاسخ‌ها را پاک کن».
 *
 * فرم با متن **اصلی** کاربر پیش‌پر می‌شود، نه با متن ترکیب‌شده‌ی تلاش قبل.
 * دلیل: نکته‌ها روی هم انبار نمی‌شوند؛ وگرنه پس از سه بازسازی، متن پرونده پر
 * از نکته‌های تکراری می‌شد.
 */

import { Wand2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Banner, Card, SectionHeader } from '@/components/ui/primitives'
import { fa } from '@/lib/format'

import type { ConsultationState } from '@/lib/types'

interface RefineFormProps {
  state: ConsultationState
  onSubmit: (payload: { revisedText: string; note: string; answers: (string | null)[] }) => void
  isBusy: boolean
}

export const RefineForm = ({ state, onSubmit, isBusy }: RefineFormProps) => {
  // `base_user_input` متن اصلی است و `raw_user_input` متن ترکیب‌شده با نکته.
  // پیش‌پر کردن با متن اصلی، انبار شدن نکته‌ها را جلوگیری می‌کند.
  const original =
    state.base_user_input === '' ? state.raw_user_input : state.base_user_input
  const [text, setText] = useState(original)
  const [note, setNote] = useState('')
  const [answers, setAnswers] = useState<string[]>(() =>
    state.clarification.map((item) => (item.is_unknown ? '' : (item.answer ?? ''))),
  )

  return (
    <Card>
      <SectionHeader
        title="بازسازی برآورد"
        caption="شرح را اصلاح کنید یا نکته‌ای اضافه کنید. برآورد از ابتدا و با کل خط لوله ساخته می‌شود."
        icon={<Wand2 className="size-4" />}
      />
      <form
        className="space-y-4 p-5"
        onSubmit={(event) => {
          event.preventDefault()
          if (text.trim() === '') {
            return
          }
          onSubmit({
            revisedText: text,
            note,
            answers: answers.map((value) => (value.trim() === '' ? null : value)),
          })
        }}
      >
        <div className="space-y-1.5">
          <label htmlFor="refine-text" className="text-xs font-semibold text-ink">
            شرح تصمیم
          </label>
          <textarea
            id="refine-text"
            value={text}
            onChange={(event) => {
              setText(event.target.value)
            }}
            rows={7}
            className="w-full resize-y rounded-md border border-line-strong bg-surface-raised px-3 py-2 text-sm leading-relaxed text-ink focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="refine-note" className="text-xs font-semibold text-ink">
            نکته یا اصلاح شما
          </label>
          <textarea
            id="refine-note"
            value={note}
            onChange={(event) => {
              setNote(event.target.value)
            }}
            rows={3}
            placeholder="مثلاً: بدهی پنهانی کشف شد که در توصیف اول نبود."
            className="w-full resize-y rounded-md border border-line-strong bg-surface-raised px-3.5 py-2.5 text-sm leading-relaxed text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none"
          />
          <p className="text-xs leading-relaxed text-ink-faint">
            نکته به‌صورت بخشی برچسب‌خورده در متن پرونده می‌نشیند، تا هم بر تحلیل و هم بر
            بازیابی اثر بگذارد و در لاگ قابل تفکیک بماند.
          </p>
        </div>

        {state.clarification.length > 0 && (
          <div className="space-y-2 rounded-md border border-line bg-surface-sunken p-3">
            <p className="text-xs font-medium text-ink-muted">
              پاسخ‌های شفاف‌سازی ({fa(state.clarification.length)} مورد)
            </p>
            <p className="text-xs leading-relaxed text-ink-faint">
              این پاسخ‌ها حفظ می‌شوند و بر اساس خودِ پرسش منتقل می‌گردند، نه شماره‌ی آن.
              اگر بررسی تازه پرسش جدیدی داشته باشد، پیش از ساخت برآورد از شما پرسیده
              می‌شود. خالی گذاشتن یک فیلد یعنی همان پاسخ را نامعلوم کنید.
            </p>
            {state.clarification.map((item, index) => {
              const inputId = `refine-answer-${String(index)}`
              return (
                <div key={item.question} className="space-y-1">
                  <label htmlFor={inputId} className="block text-xs text-ink">
                    {item.question}
                  </label>
                  <input
                    id={inputId}
                    value={answers[index] ?? ''}
                    onChange={(event) => {
                      setAnswers((current) =>
                        current.map((value, position) =>
                          position === index ? event.target.value : value,
                        ),
                      )
                    }}
                    placeholder="خالی بگذارید تا نامعلوم بماند"
                    className="h-8 w-full rounded-sm border border-line-strong bg-surface-raised px-2 text-xs text-ink placeholder:text-ink-faint focus:border-accent focus:outline-none"
                  />
                </div>
              )
            })}
          </div>
        )}

        <Banner tone="warning">
          برآورد قبلی به مدل داده نمی‌شود و در آرشیو دست‌نخورده می‌ماند؛ ارزیابی تازه
          مستقل است. اما پاسخ‌های شفاف‌سازی شما منتقل می‌شوند و دوباره پرسیده نمی‌شوند.
        </Banner>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={isBusy}
            disabled={text.trim() === ''}
            icon={<Wand2 className="size-4" aria-hidden />}
          >
            ساخت برآورد تازه
          </Button>
        </div>
      </form>
    </Card>
  )
}
